import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styles from './TestResult.module.scss';
// COMPs
import {
  Button,
  ButtonEmpty,
  FieldText,
  FilePicker,
  FlexGroup,
  FlexItem,
  Flyout,
  FlyoutBody,
  FlyoutFooter,
  FlyoutHeader,
  FormRow,
  notification,
  Outline,
  Text,
} from '@antoree/ant-ui';

import { useToggle } from 'hooks';
import {
  deleteHomeworkVideo,
  Session,
  useSendTestResult,
} from 'services/session';
import { useUploadSessionVideo } from 'services/session/uploadSessionVideo';
import {
  AdultScore,
  FormTestResult,
  HeaderTestResult,
  YoungLearnerScore,
} from './components';

import {
  adultConfGrade,
  adultType,
  childType,
  FORM_TEST_RESULT_ID,
  kidConfGrade,
} from './constant';
import { TESTING_TYPE } from 'constants/courses';
import { useEditTestResult } from 'services/session/editTestResult';
import { useRetrieveTestResult } from 'services/session/retrieveTestResult';
import { withDebounce } from '@antoree/helpers';
import {
  deleteTestResultVideo,
  useDeleteTestResultVideo,
} from 'services/session/deleteTestResultVideo';
import { customRound } from '../utils/sessionContent';
import { useScreenRecorder } from 'contexts/ScreenRecorderContext';
import { useAutoUploadRecordedVideo } from '../SessionContent/hooks/useAutoUploadRecordedVideo';
import { API_SERVICE_URL } from 'configs/env.conf';
import { IFormData as TestResultFormData } from './components/FormTestResult';

export type TestResultProps = {
  onReload: () => void;
  session: Session;
  autoAttachRecordedVideo?: boolean;
};
export type TestResultHandle = {
  open: () => void;
};
export type Tab = {
  id: number;
  name: string;
};

import { useIntl } from 'react-intl';

const TestResult = forwardRef<TestResultHandle, TestResultProps>(
  ({ onReload, session, autoAttachRecordedVideo = false }, ref) => {
    const intl = useIntl();
    const { isVisiable, close, open } = useToggle();
    const {
      isVisiable: isVisibleEdit,
      close: closeEdit,
      open: openEdit,
    } = useToggle();
    const [totalScore, setTotalCore] = useState<Number | string>(0);
    const [link, setLink] = useState('');
    const { recordedVideo } = useScreenRecorder();

    const openTestResultFlyout = useCallback(() => {
      setHasCalledAnalyzeApi(false);
      if (session?.test) {
        openEdit();
        return;
      }
      open();
    }, [open, openEdit, session?.test]);

    useImperativeHandle(
      ref,
      () => ({
        open: openTestResultFlyout,
      }),
      [openTestResultFlyout],
    );

    const tabs: Tab[] = [
      {
        id: childType,
        name: intl.formatMessage({
          id: 'test_result.tab.young_learners',
          defaultMessage: 'Young learners (Less than 13 years old)',
        }),
      },
      {
        id: adultType,
        name: intl.formatMessage({
          id: 'test_result.tab.adults',
          defaultMessage: 'Teenagers & Adult (13 years old and more)',
        }),
      },
    ];
    const {
      data: dataTestResult,
      refetch: refetchTestResult,
    } = useRetrieveTestResult(
      { courseId: session.course_id },
      {
        enabled: isVisiable || isVisibleEdit,
        refetchOnMount: true,
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        retry: false,
      },
    );
    const { mutate, isLoading } = useSendTestResult({
      onSuccess: () => {
        notification.success({
          title: <FormattedMessage defaultMessage="Send test result success" />,
        });
        onReload();
        close();
      },
      onError: error => {
        const message = error?.response?.data?.errors[0].message;
        notification.error({
          title: <FormattedMessage defaultMessage="Send test result fail" />,
          text: message || 'Send test result failure!',
        });
      },
    });
    const {
      mutate: mutateDeleteTestResult,
      isLoading: isLoadingDeleteTestResult,
    } = useDeleteTestResultVideo({});
    const {
      mutate: mutateEditTestResult,
      isLoading: isLoadingEditTestResult,
    } = useEditTestResult({
      onSuccess: () => {
        notification.success({
          title: <FormattedMessage defaultMessage="Edit test result success" />,
        });
        onReload();
        closeEdit();
      },
      onError: error => {
        const message = error?.response?.data?.errors[0].message;
        notification.error({
          title: <FormattedMessage defaultMessage="Edit test result fail" />,
          text: message || 'Edit test result failure!',
        });
      },
    });

    const {
      mutate: uploadSessionVideoMutation,
      mutateAsync: uploadSessionVideoMutationAsync,
      isLoading: isLoadingUploadSessionVideo,
    } = useUploadSessionVideo({
      onSuccess: () => {
        notification.success({
          title: <FormattedMessage defaultMessage="Upload video success" />,
        });
      },
      onError: err => {
        const mesError = err?.response?.data?.errors[0]?.message;

        notification.error({
          title: <FormattedMessage defaultMessage="Error" />,
          text: mesError || (
            <FormattedMessage defaultMessage="Upload video failed" />
          ),
        });
      },
    });

    const [selectedTabId, setSelectedTabId] = useState(childType);
    const [
      autoFillData,
      setAutoFillData,
    ] = useState<Partial<TestResultFormData> | null>(null);
    const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');
    const [
      pendingAnalyzeVideoFile,
      setPendingAnalyzeVideoFile,
    ] = useState<File | null>(null);
    const [isAnalyzingVideo, setIsAnalyzingVideo] = useState(false);
    const [hasCalledAnalyzeApi, setHasCalledAnalyzeApi] = useState(false);

    const onSelectedTabChanged = (id: number) => {
      setSelectedTabId(id);
    };
    const level = useMemo(() => {
      const arr = selectedTabId !== childType ? adultConfGrade : kidConfGrade;

      return (
        arr.find(item => customRound(+totalScore) <= item.maxPoint)?.label || ''
      );
    }, [totalScore]);

    // Validate video file format
    const validateVideoFile = (file: File): boolean => {
      const allowedExtensions = [
        '.mp4',
        '.avi',
        '.mkv',
        '.webm',
        '.3gp',
        '.ogv',
        '.flv',
      ];
      const allowedMimeTypes = [
        'video/mp4',
        'video/x-msvideo', // avi
        'video/x-matroska', // mkv
        'video/webm',
        'video/3gpp', // 3gp
        'video/ogg', // ogv
        'video/x-flv', // flv
      ];

      // Check by file extension
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext =>
        fileName.endsWith(ext),
      );

      // Check by MIME type
      const hasValidMimeType =
        Boolean(file.type) && allowedMimeTypes.includes(file.type);

      return hasValidExtension || hasValidMimeType;
    };

    const handleUploadSessionVideo = (videos: FileList | null) => {
      if (videos && videos[0]) {
        const videoFile = videos[0];

        // Validate video file format
        if (!validateVideoFile(videoFile)) {
          setPendingAnalyzeVideoFile(null);
          setHasCalledAnalyzeApi(false);
          notification.error({
            title: <FormattedMessage defaultMessage="Invalid video format" />,
            text: (
              <FormattedMessage defaultMessage="Please upload a video file with one of these formats: mp4, avi, mkv, webm, 3gp, ogv, flv" />
            ),
          });
          return;
        }

        // Allow teacher to grade immediately after file selection,
        // without waiting for the upload request to complete.
        setPendingAnalyzeVideoFile(videoFile);
        setHasCalledAnalyzeApi(false);

        const formData = new FormData();
        formData.append('attachment-video', videoFile);

        // 1) Upload video to session content (R2)
        uploadSessionVideoMutation({
          id: session.id,
          courseId: session.course_id,
          formData,
        });

        // Background analyze is disabled.
        // Grading now only runs when teacher explicitly clicks the grade button.
      }
    };

    const toFieldValue = (raw: any) => {
      if (raw === undefined || raw === null) return '';
      return typeof raw === 'string' ? raw : String(raw);
    };

    const toScore = (raw: any) => {
      if (raw === undefined || raw === null || Number.isNaN(Number(raw)))
        return 0;
      return Number(raw);
    };

    const getAssessmentProfile = useCallback(
      () => (selectedTabId === childType ? 'kid' : 'adult'),
      [selectedTabId],
    );

    const analyzeVideoAndAutofill = async (videoFile: File) => {
      try {
        setIsAnalyzingVideo(true);
        setHasCalledAnalyzeApi(true);
        const formData = new FormData();
        formData.append('video', videoFile);
        formData.append('courseId', String(session.course_id));
        formData.append('profile', getAssessmentProfile());
        formData.append('model', selectedModel);

        const response = await fetch(
          `${API_SERVICE_URL}/v2/video-assessment/analyze-test`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
            },
            body: formData,
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Analyze-test request failed');
        }

        const result = await response.json();
        const reviewDetails = result?.reviewDetails || {};
        const fluency = reviewDetails?.avoidSilenceOrHesitation?.criteria || [];
        const vocabulary = reviewDetails?.vocabulary?.criteria || [];
        const grammar = reviewDetails?.grammarAndAccuracy?.criteria || [];
        const pronunciation = reviewDetails?.pronunciation?.criteria || [];
        const general = result?.generalAssessment || {};

        setAutoFillData({
          fluency1Score: toScore(fluency?.[0]?.score),
          fluency1: toFieldValue(fluency?.[0]?.comment),
          fluency2Score: toScore(fluency?.[1]?.score),
          fluency2: toFieldValue(fluency?.[1]?.comment),
          fluency3Score: toScore(fluency?.[2]?.score),
          fluency3: toFieldValue(fluency?.[2]?.comment),
          lexical1Score: toScore(vocabulary?.[0]?.score),
          lexical1: toFieldValue(vocabulary?.[0]?.comment),
          lexical2Score: toScore(vocabulary?.[1]?.score),
          lexical2: toFieldValue(vocabulary?.[1]?.comment),
          lexical3Score: toScore(vocabulary?.[2]?.score),
          lexical3: toFieldValue(vocabulary?.[2]?.comment),
          grammatical1Score: toScore(grammar?.[0]?.score),
          grammatical1: toFieldValue(grammar?.[0]?.comment),
          grammatical2Score: toScore(grammar?.[1]?.score),
          grammatical2: toFieldValue(grammar?.[1]?.comment),
          grammatical3Score: toScore(grammar?.[2]?.score),
          grammatical3: toFieldValue(grammar?.[2]?.comment),
          pronunciation1Score: toScore(pronunciation?.[0]?.score),
          pronunciation1: toFieldValue(pronunciation?.[0]?.comment),
          pronunciation2Score: toScore(pronunciation?.[1]?.score),
          pronunciation2: toFieldValue(pronunciation?.[1]?.comment),
          pronunciation3Score: toScore(pronunciation?.[2]?.score),
          pronunciation3: toFieldValue(pronunciation?.[2]?.comment),
          comment1: toFieldValue(general?.strongPoint),
          comment2: toFieldValue(general?.needToImprove),
          comment3: toFieldValue(general?.recommendation),
          personalCriteria1: toFieldValue(general?.attitudeTowardsLearning),
          personalCriteria2: toFieldValue(general?.secondLanguageAcquisition),
        });

        notification.success({
          title: (
            <FormattedMessage
              id="test_result.ai_analysis.autofill_success"
              defaultMessage="Auto-filled result from video analysis"
            />
          ),
        });
      } catch (error) {
        console.error('analyzeVideoAndAutofill error:', error);
        notification.error({
          title: (
            <FormattedMessage
              id="test_result.ai_analysis.failed"
              defaultMessage="Video analysis failed"
            />
          ),
        });
      } finally {
        setIsAnalyzingVideo(false);
      }
    };

    const triggerBackgroundSystemAnalyze = useCallback(async () => {
      if (!pendingAnalyzeVideoFile || hasCalledAnalyzeApi) return;
      setHasCalledAnalyzeApi(true);

      try {
        const body = new FormData();
        body.append('video', pendingAnalyzeVideoFile);
        body.append('courseId', String(session.course_id));
        body.append('profile', getAssessmentProfile());

        const res = await fetch(
          `${API_SERVICE_URL}/v2/video-assessment/analyze-test`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
            },
            body,
          },
        );

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'System analyze-test failed');
        }
      } catch (error) {
        console.error(
          '[Teacher] triggerBackgroundSystemAnalyze on close error',
          error,
        );
      }
    }, [
      pendingAnalyzeVideoFile,
      hasCalledAnalyzeApi,
      session.course_id,
      getAssessmentProfile,
    ]);

    const handleAutoUploadSessionVideo = useCallback(
      async (videos: FileList) => {
        if (!videos || !videos[0]) return;
        const videoFile = videos[0];

        if (!validateVideoFile(videoFile)) {
          notification.error({
            title: <FormattedMessage defaultMessage="Invalid video format" />,
            text: (
              <FormattedMessage defaultMessage="Please upload a video file with one of these formats: mp4, avi, mkv, webm, 3gp, ogv, flv" />
            ),
          });
          return;
        }

        const formData = new FormData();
        formData.append('attachment-video', videoFile);
        setPendingAnalyzeVideoFile(videoFile);
        setHasCalledAnalyzeApi(false);
        await uploadSessionVideoMutationAsync({
          id: session.id,
          courseId: session.course_id,
          formData,
        });

        // Background analyze is disabled.
        // Grading now only runs when teacher explicitly clicks the grade button.
      },
      [session.id, session.course_id, uploadSessionVideoMutationAsync],
    );

    const handleCloseFlyout = useCallback(() => {
      if (!hasCalledAnalyzeApi) {
        void triggerBackgroundSystemAnalyze();
      }

      if (isVisiable) {
        close();
        return;
      }

      closeEdit();
    }, [
      hasCalledAnalyzeApi,
      triggerBackgroundSystemAnalyze,
      isVisiable,
      close,
      closeEdit,
    ]);

    useAutoUploadRecordedVideo({
      recordedVideo: autoAttachRecordedVideo ? recordedVideo : null,
      sessionId: session.id,
      onUpload: handleAutoUploadSessionVideo,
      onOpenForm: openTestResultFlyout,
    });
    const handleDeleteVideo = (index: number) => {
      const indices: number[] = [];
      if (index !== -1 && index !== undefined) {
        indices.push(index);
      }
      if (indices.length > 0) {
        // isTriggerFile.current = true;
        mutateDeleteTestResult({
          id: session.id,
          courseId: session.course_id,
          indices,
        });
      }
    };
    useEffect(() => {
      if (dataTestResult) {
        // Only sync tab from stored test result when editing existing data.
        // In add mode, keep the teacher's currently selected tab.
        if (isVisibleEdit) {
          onSelectedTabChanged(
            dataTestResult?.data.data?.test_result_url?.testType || 1,
          );
        }
        setLink(dataTestResult?.data.data.test_result_url?.link);
      }
    }, [dataTestResult, isVisibleEdit]);
    console.log(selectedTabId);

    return (
      <>
        {!session?.test ? (
          <div className={styles.testResult}>
            <ButtonEmpty onClick={() => open()}>
              <FormattedMessage
                id="test_result.add_result"
                defaultMessage="Add result"
              />
            </ButtonEmpty>
            <span className="text-danger">*</span>
          </div>
        ) : (
          <div className="flex">
            <ButtonEmpty
              onClick={() => window.open(session?.test?.url, '_blank')}
              className="h-6"
              iconType="checkInCircleFilled"
              color="text"
            >
              <FormattedMessage
                id="test_result.view_result"
                defaultMessage="View result"
              />
            </ButtonEmpty>
            <ButtonEmpty
              onClick={() => openEdit()}
              className="h-6"
              iconType="eraser"
              color="text"
            >
              <FormattedMessage
                id="test_result.edit_result"
                defaultMessage="Edit result"
              />
            </ButtonEmpty>
          </div>
        )}

        {(isVisiable || isVisibleEdit) && (
          <Flyout
            style={{ width: '1220px' }}
            ownFocus
            outsideClickCloses={false}
            onClose={handleCloseFlyout}
          >
            <HeaderTestResult
              onSelectedTabChanged={onSelectedTabChanged}
              selectedTabId={selectedTabId}
              tabs={tabs}
              student={session?.student}
              isVisibleEdit={isVisibleEdit}
            />
            <FlyoutBody>
              <>
                <FlexGroup direction="column">
                  <FlexItem>
                    <FormRow fullWidth>
                      <FilePicker
                        display="large"
                        compressed
                        // multiple
                        initialPromptText={
                          <p>
                            <FormattedMessage
                              id="common.upload_files"
                              defaultMessage="Upload files"
                            />
                          </p>
                        }
                        fullWidth
                        accept="video/mp4,video/x-msvideo,video/x-matroska,video/webm,video/3gpp,video/ogg,video/x-flv,.mp4,.avi,.mkv,.webm,.3gp,.ogv,.flv"
                        onChange={handleUploadSessionVideo}
                        isLoading={isLoadingUploadSessionVideo}
                        disabled={isLoadingUploadSessionVideo}
                        readOnly={isLoadingUploadSessionVideo}
                      />
                    </FormRow>
                    <Text color="subdued" style={{ fontSize: '14px' }}>
                      <p>
                        <FormattedMessage
                          id="test_result.video_format_warning"
                          defaultMessage="The attachment video must be a file of type: mp4, avi, mkv, webm, 3gp, ogv, flv."
                        />
                      </p>
                    </Text>
                    {dataTestResult?.data.data?.video_url &&
                      dataTestResult.data.data.video_url.map((item, index) => {
                        const ext =
                          item.split('?')[0].split('.').pop()?.toLowerCase() ||
                          'webm';
                        const fileName = `Antoree_Recording_${session.id}_${
                          index + 1
                        }.${ext}`;
                        return (
                          <div
                            className="flex mt-2 gap-4 items-center"
                            key={index}
                          >
                            <FlexItem grow={false}>
                              <video
                                width="240"
                                height="240"
                                controls
                                className="rounded-lg"
                                preload="metadata"
                                onError={e => {
                                  console.error('Video playback error:', e);
                                  const video = e.target as HTMLVideoElement;
                                  if (
                                    video.error?.code ===
                                    video.error?.MEDIA_ERR_SRC_NOT_SUPPORTED
                                  ) {
                                    console.error(
                                      'Video format not supported or file corrupted',
                                    );
                                  }
                                }}
                              >
                                <source src={item} type="video/mp4" />
                                <source src={item} type="video/webm" />
                                Your browser does not support the video tag.
                              </video>
                            </FlexItem>

                            <FlexItem grow={false}>
                              <FlexGroup direction="column" gutterSize="s">
                                <FlexItem grow={false}>
                                  <a
                                    href={item}
                                    download={fileName}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <ButtonEmpty iconType="download">
                                      <FormattedMessage
                                        id="common.download"
                                        defaultMessage="Download"
                                      />
                                    </ButtonEmpty>
                                  </a>
                                </FlexItem>
                                <FlexItem grow={false}>
                                  <ButtonEmpty
                                    onClick={() => handleDeleteVideo(index)}
                                    isLoading={isLoadingDeleteTestResult}
                                    iconType="cross"
                                  >
                                    <FormattedMessage
                                      id="common.remove"
                                      defaultMessage="Remove"
                                    />
                                  </ButtonEmpty>
                                </FlexItem>
                              </FlexGroup>
                            </FlexItem>
                          </div>
                        );
                      })}
                  </FlexItem>
                  <FlexItem>
                    <FormRow
                      fullWidth
                      label={
                        <FormattedMessage
                          id="test_result.ai_analysis.reasoning_type"
                          defaultMessage="Kiểu suy luận"
                        />
                      }
                    >
                      <FlexGroup gutterSize="s" alignItems="flexEnd">
                        <FlexItem>
                          <select
                            value={selectedModel}
                            onChange={event =>
                              setSelectedModel(event.target.value)
                            }
                            style={{
                              width: '100%',
                              height: 40,
                              border: '1px solid #d3dae6',
                              borderRadius: 6,
                              padding: '0 12px',
                              background: '#fff',
                            }}
                          >
                            <option value="gemini-2.5-flash">
                              {intl.formatMessage({
                                id: 'test_result.ai_analysis.fast_reasoning',
                                defaultMessage: 'Suy luận nhanh (3-4p)',
                              })}
                            </option>
                            <option value="gemini-3.1-pro-preview">
                              {intl.formatMessage({
                                id:
                                  'test_result.ai_analysis.detailed_reasoning',
                                defaultMessage: 'Suy luận chi tiết (5-7p)',
                              })}
                            </option>
                          </select>
                        </FlexItem>
                        <FlexItem grow={false}>
                          <Button
                            fill
                            onClick={() => {
                              if (!pendingAnalyzeVideoFile) {
                                notification.error({
                                  title: (
                                    <FormattedMessage
                                      id="test_result.ai_analysis.upload_first"
                                      defaultMessage="Please upload a video first"
                                    />
                                  ),
                                });
                                return;
                              }
                              analyzeVideoAndAutofill(pendingAnalyzeVideoFile);
                            }}
                            disabled={!pendingAnalyzeVideoFile}
                            isLoading={isAnalyzingVideo}
                          >
                            <FormattedMessage
                              id="test_result.ai_analysis.grade_button"
                              defaultMessage="Chấm video bằng hệ thống"
                            />
                          </Button>
                        </FlexItem>
                      </FlexGroup>
                    </FormRow>
                    <Text color="subdued" style={{ fontSize: '14px' }}>
                      <p>
                        <FormattedMessage
                          id="test_result.ai_analysis.hint"
                          defaultMessage="Bạn có thể chọn model trước. Khi đã chọn file video thì bấm nút để bắt đầu chấm."
                        />
                      </p>
                      <p
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: '#B54708',
                          fontWeight: 500,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            backgroundColor: '#FDE68A',
                            color: '#92400E',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                          aria-hidden="true"
                        >
                          !
                        </span>
                        <FormattedMessage
                          id="test_result.ai_analysis.disclaimer"
                          defaultMessage="Note: System results are for reference only. Teachers are professionally responsible for the final result before sharing it with students."
                        />
                      </p>
                    </Text>
                  </FlexItem>
                  <FlexItem>
                    <FormRow
                      fullWidth
                      label={
                        <FormattedMessage
                          id="common.link"
                          defaultMessage="Link"
                        />
                      }
                    >
                      <FieldText
                        fullWidth
                        onChange={withDebounce(event => {
                          setLink(event.target.value);
                        })}
                        value={link}
                      />
                    </FormRow>
                  </FlexItem>
                </FlexGroup>
                {selectedTabId == childType ? (
                  <>
                    <YoungLearnerScore />
                  </>
                ) : (
                  <>
                    <AdultScore />
                  </>
                )}
                <FormTestResult
                  onChangeTotalScore={(value: number | string) =>
                    setTotalCore(value)
                  }
                  isAdult={selectedTabId != childType || false}
                  mutate={mutate}
                  mutateEdit={mutateEditTestResult}
                  id={session?.id}
                  isCourseUnit={session.isCourseUnit}
                  link={link}
                  testResult={dataTestResult?.data.data.test_result_url}
                  isCourseTest={session.courseType === TESTING_TYPE}
                  testType={selectedTabId}
                  isVisibleEdit={isVisibleEdit}
                  testId={session?.test?.id}
                  autoFillData={autoFillData}
                />
              </>
            </FlyoutBody>
            <FlyoutFooter style={{ backgroundColor: '#fff' }}>
              <FlexGroup>
                <FlexItem grow={9}>
                  <FlexGroup>
                    <FlexItem grow={false}>
                      <Text>
                        <p>
                          <FormattedMessage
                            id="test_result.total_score"
                            defaultMessage="Total score"
                          />
                          :{` ${totalScore}`}
                        </p>
                      </Text>
                    </FlexItem>
                    <FlexItem grow={false}>
                      <Text>
                        <p>
                          <FormattedMessage
                            id="claim_request.detail.level"
                            defaultMessage="Level"
                          />
                          : {level}
                        </p>
                      </Text>
                    </FlexItem>
                  </FlexGroup>
                </FlexItem>
                <FlexItem grow={1}>
                  <div className="flex">
                    <ButtonEmpty className="mr-4" onClick={handleCloseFlyout}>
                      <FormattedMessage
                        id="common.cancel"
                        defaultMessage="Cancel"
                      />
                    </ButtonEmpty>
                    <Button
                      fill
                      type="submit"
                      form={FORM_TEST_RESULT_ID}
                      isLoading={isLoading || isLoadingEditTestResult}
                    >
                      <FormattedMessage
                        id="common.submit"
                        defaultMessage="Submit"
                      />
                    </Button>
                  </div>
                </FlexItem>
              </FlexGroup>
            </FlyoutFooter>
          </Flyout>
        )}
      </>
    );
  },
);

export default TestResult;
